import { groupBy } from 'lodash-es';
import type { GlobalFilter, FilterType } from '@hotjar/global-filters';
import {
  FilterKindEnum,
  URLConverter,
  FeedbackConverter,
  NPSConverter,
  ClickedConverter,
  ErrorConverter,
  convertFromFilterTypeToQueryFormat,
  LargeListFilterOperation,
  RAGE_CLICK_TYPE,
  U_TURN_TYPE,
} from '@hotjar/global-filters';

export function getParameterByName(name: string, url: string = window.location.href): string {
  const parsedName = name.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${parsedName}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) return '';
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export const getRecordingId = (): number => {
  return parseInt(getParameterByName('recording'), 10);
};

export const getSiteId = (): number => {
  return parseInt(getParameterByName('site'), 10);
};

export const getToken = (): string => {
  return getParameterByName('token');
};

export const getIsRetakerMode = (): boolean => {
  return Boolean(getParameterByName('forRetaker'));
};

export const getDebug = (): boolean => {
  // Turn on console debugging for drawing elements during playback.
  // ?debugCreateElement=true
  return getParameterByName('debugCreateElement') === 'true';
};

const RECOGNISED_FILTER: FilterKindEnum[] = [
  FilterKindEnum.VisitedURL,
  FilterKindEnum.TeamComment,
  FilterKindEnum.Event,
  FilterKindEnum.RageClickKind,
  FilterKindEnum.UTurnKind,
  FilterKindEnum.NPS,
  FilterKindEnum.IncomingFeedback,
  FilterKindEnum.Clicked,
  FilterKindEnum.Error,
  FilterKindEnum.Funnel,
];

function tryGetRecordingFilters(): GlobalFilter {
  try {
    const recordingFilters = localStorage.getItem('recordingFiltersUiFormat');
    if (recordingFilters) {
      return JSON.parse(recordingFilters);
    }

    return {
      filterList: [],
    };
  } catch {
    //
  }
  return {
    filterList: [],
  };
}

/**
 * Gets the filters we care about from the recording list filters.
 */
function getApplicableFilters(): {
  [index: string]: FilterType[];
} {
  const rawFilters = tryGetRecordingFilters();
  const applicableFilters = rawFilters.filterList.filter((filter: FilterType) =>
    RECOGNISED_FILTER.includes(filter.kind),
  );

  const groupedFilters = groupBy(applicableFilters, 'kind');

  return groupedFilters;
}

// Parse page filters to send them to the Recording endpoint.
// Results in a list of objects, e.g. [{ starts_with: 'https' }, { contains: '/dashboard' }].
/**
 * @function parsePageFilters
 * Parses pafe filters to send them to the Recording endpoint
 * @return {Object[]}
 * @example [{ starts_with: 'https' }, { contains: '/dashboard' }]
 */
export const parsePageFilters = (filters: FilterType[]): Record<string, string>[] | null => {
  if (!filters || !filters.length) return null;

  return filters.reduce((acc: Array<Record<string, string>>, filter: FilterType) => {
    if (filter.kind !== FilterKindEnum.VisitedURL || !filter.value) {
      return acc;
    }

    const { operation, negate } = URLConverter.convertUiOperation(filter.value.operation);

    if (
      !operation ||
      negate ||
      // URLFilter component use a RadioTextFilterValue type wich can be null for typescript reason so we have to check it
      !('value' in filter.value)
    ) {
      return acc;
    }

    // The backend only understand lower case operation for page which is weird because:
    // - NPS and IF filter are uppercased
    // - Those page operations are uppercase everywhere we use the global filter
    // => We will do this temporary hack until the backend is adapted.
    const lowerCasedOperation = operation.toLocaleLowerCase();
    const obj = { [lowerCasedOperation]: filter.value.value };
    acc.push(obj);
    return acc;
  }, []);
};

/**
 * @function parseEventFilters
 * Parses event  filters to send them to the Recording endpoint
 * @return {string[]}
 * @example ['event1', 'event2']
 */
export const parseEventFilters = (filters: FilterType[]): string[] | null => {
  if (!filters || !filters.length) return null;

  return filters.reduce((acc: string[], filter: FilterType) => {
    if (
      filter.kind === FilterKindEnum.Event &&
      filter.value &&
      filter.value.operation === LargeListFilterOperation.IS_ANY_OF_THESE
    ) {
      acc.push(...filter.value.selectedValues);
    }
    // remove duplicate values in event filters
    return [...new Set(acc)];
  }, []);
};

/**
 * @function parseBooleanFilters
 * Parses boolean filters (u-turn, rageclick) to send them to the Recording endpoint.
 * It only returns the filters where the value is true
 * @return {string}
 * @example true
 */
export const parseBooleanFilters = (filters: FilterType[]): boolean | null => {
  if (!filters || filters.length < 1) return null;
  const filter = filters[0];
  if (filter.kind === FilterKindEnum.RageClickKind) {
    return filter.value === RAGE_CLICK_TYPE.OCCURRED || null;
  }
  if (filter.kind === FilterKindEnum.UTurnKind) {
    return filter.value === U_TURN_TYPE.OCCURRED || null;
  }
  return null;
};

/**
 * @function parseIFFilters
 * Parses IF filters to send them to the Recording endpoint
 * @return {Object[]}
 * @example IF: {"OR":[{"EQUAL":{"incoming_feedback":"feedback.sentiment.e:4"}},{"EQUAL":{"incoming_feedback":"feedback.sentiment.e:3"}}]}
 */
export const parseIFFilters = (filters: FilterType[]): Record<string, unknown> | null => {
  if (!filters || filters.length < 1) return null;
  const filter = filters[0];
  if (filter.kind === FilterKindEnum.IncomingFeedback) {
    const filterValue = FeedbackConverter.fromUiToQueryFormat(filter.value);
    return filterValue;
  }
  return null;
};

/**
 * @function parseNPSFilters
 * Parses NPS filters to send them to the Recording endpoint
 * @return {Object[]}
 * @example NPS: {"OR":[{"EQUAL":{"nps":"poll.q.t:nps.a:9"}},{"EQUAL":{"nps":"poll.q.t:nps.a:10"}},{"EQUAL":{"nps":"poll.q.t:nps.a:7"}},{"EQUAL":{"nps":"poll.q.t:nps.a:8"}}]}
 */
export const parseNPSFilters = (filters: FilterType[]): Record<string, unknown> | null => {
  if (!filters || filters.length < 1) return null;
  const filter = filters[0];
  if (filter.kind === FilterKindEnum.NPS) {
    const filterValue = NPSConverter.fromUiToQueryFormat(filter.value);
    return filterValue;
  }
  return null;
};

/**
 * @function parseClickedFilters
 * Parses clicked filters to send them to the Recording endpoint
 * @return {Object[]}
 * @example
 */
export const parseClickedFilters = (filters: FilterType[]): Record<string, unknown>[] | null => {
  if (!filters || filters.length < 1) return null;

  const clickedFilters = filters.reduce(
    (acc: Record<string, unknown>[], currentFilter: FilterType) => {
      if (currentFilter.kind === FilterKindEnum.Clicked && currentFilter.value) {
        acc.push(ClickedConverter.fromUiToQueryFormat(currentFilter.value));
      }
      return acc;
    },
    [],
  );

  return clickedFilters.length > 0 ? clickedFilters : null;
};

/**
 * @function parseErrorFilters
 * Parses error filters to send them to the Recording endpoint
 * @return {Object[]}
 * @example
 */
export const parseErrorFilters = (filters: FilterType[]): Record<string, unknown>[] | null => {
  if (!filters || filters.length < 1) return null;

  const errorFilters = filters.reduce(
    (acc: Record<string, unknown>[], currentFilter: FilterType) => {
      if (currentFilter.kind === FilterKindEnum.Error && currentFilter.value) {
        acc.push(ErrorConverter.fromUiToQueryFormat(currentFilter.value));
      }
      return acc;
    },
    [],
  );

  return errorFilters.length > 0 ? errorFilters : null;
};

/**
 * @function parseFunnelsFilters
 * Parses funnels filters to send them to the Recording endpoint
 * @return {Object[]}
 * @example
 */
export const parseFunnelsFilters = (filters: FilterType[]): Record<string, unknown>[] | null => {
  if (!filters || filters.length < 1) return null;

  const funnelsFilters = filters.reduce(
    (acc: Record<string, unknown>[], currentFilter: FilterType) => {
      if (currentFilter.kind === FilterKindEnum.Funnel && currentFilter.value) {
        const queryFormat = convertFromFilterTypeToQueryFormat([currentFilter]);
        acc.push(queryFormat);
      }
      return acc;
    },
    [],
  );

  return funnelsFilters.length > 0 ? funnelsFilters : null;
};

export const getAllFiltersQueryParams = (): Record<string, unknown> => {
  const groupedFilters = getApplicableFilters();

  const pageFilters = parsePageFilters(groupedFilters[FilterKindEnum.VisitedURL] || []);
  const eventFilters = parseEventFilters(groupedFilters[FilterKindEnum.Event] || []);
  const rageclickFilters = parseBooleanFilters(groupedFilters[FilterKindEnum.RageClickKind] || []);
  const uturnFilters = parseBooleanFilters(groupedFilters[FilterKindEnum.UTurnKind] || []);
  const npsFilters = parseNPSFilters(groupedFilters[FilterKindEnum.NPS] || []);
  const ifFilters = parseIFFilters(groupedFilters[FilterKindEnum.IncomingFeedback] || []);
  const clickedFilters = parseClickedFilters(groupedFilters[FilterKindEnum.Clicked] || []);
  const errorFilters = parseErrorFilters(groupedFilters[FilterKindEnum.Error] || []);
  const funnelsFilters = parseFunnelsFilters(groupedFilters[FilterKindEnum.Funnel] || []);

  const allFilters = {
    ...(pageFilters && { page_filters: JSON.stringify(pageFilters) }),
    ...(eventFilters && { event_filters: JSON.stringify(eventFilters) }),
    ...(rageclickFilters && { rageclick_filters: rageclickFilters }),
    ...(uturnFilters && { uturn_filters: uturnFilters }),
    ...(npsFilters && { nps_filters: JSON.stringify(npsFilters) }),
    ...(ifFilters && { if_filters: JSON.stringify(ifFilters) }),
    ...(clickedFilters && { clicked_filters: JSON.stringify(clickedFilters) }),
    ...(errorFilters && { error_filters: JSON.stringify(errorFilters) }),
    ...(funnelsFilters && { funnel_filters: JSON.stringify(funnelsFilters[0]) }),
  };
  return allFilters;
};

/**
 * Get the parts of the URL needed for tracking `url_event_was_triggered_on`
 *
 * Example `/r?site=1&recording=1&opener=recordings-list`
 *
 * @returns string
 */
export const getUrl = (): string => window.location.pathname + window.location.search;
